import React, { useState, useCallback, useMemo } from 'react';
import {
  Form,
  DatePicker,
  InputNumber,
  Button,
  Space,
  Row,
  Col,
  message,
  Card,
  Divider,
  Typography,
  Tag,
  Collapse,
  Alert,
} from 'antd';
import {
  SaveOutlined,
  ClearOutlined,
  PlusOutlined,
  CopyOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import {
  useMassSetBaselineForecasts,
  type RevenueCreateUpdate,
} from '@/services';

interface DailyForecast {
  date: string;
  forecast: number;
}

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;
const { Panel } = Collapse;

// Daily forecast input component
const DailyForecastInput: React.FC<{
  item: DailyForecast;
  onChange: (date: string, value: number) => void;
  formatter: (value: any) => string;
  parser: (value: any) => number;
}> = ({ item, onChange, formatter, parser }) => {
  return (
    <Col span={8} key={item.date}>
      <div style={{ marginBottom: 8 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
          <Text strong style={{ fontSize: '12px' }}>
            {dayjs(item.date).format('MMM DD')}
          </Text>
          <Tag color={dayjs(item.date).day() === 0 || dayjs(item.date).day() === 6 ? 'orange' : 'blue'}>
            {dayjs(item.date).format('ddd')}
          </Tag>
        </div>
        <InputNumber
          value={item.forecast}
          onChange={(value) => onChange(item.date, value || 0)}
          min={0}
          step={100}
          precision={2}
          style={{ width: '100%' }}
          size="small"
          placeholder="0"
          formatter={formatter}
          parser={parser}
        />
      </div>
    </Col>
  );
};

interface BaselineForecastFormProps {
  onSuccess: () => void;
}

const BaselineForecastForm: React.FC<BaselineForecastFormProps> = ({ onSuccess }) => {
  // Daily forecast form state
  const [selectedDateRange, setSelectedDateRange] = useState<[dayjs.Dayjs, dayjs.Dayjs] | null>(null);
  const [dailyForecasts, setDailyForecasts] = useState<DailyForecast[]>([]);
  const [bulkFillValue, setBulkFillValue] = useState<number>();

  const massSetMutation = useMassSetBaselineForecasts();

  // Currency formatters
  const currencyFormatter = useCallback((value: any) => {
    if (!value) return '';
    return `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }, []);

  const currencyFormatterWithSymbol = useCallback((value: any) => {
    if (!value) return '';
    return `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }, []);

  const currencyParser = useCallback((value: any) => {
    return Number(value?.replace(/\$\s?|(,*)/g, '')) || 0;
  }, []);

  // Generate daily forecasts when date range changes
  const generateDailyForecasts = useCallback((startDate: dayjs.Dayjs, endDate: dayjs.Dayjs) => {
    const forecasts: DailyForecast[] = [];
    let currentDate = startDate.clone();
    
    while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
      forecasts.push({
        date: currentDate.format('YYYY-MM-DD'),
        forecast: 0,
      });
      currentDate = currentDate.add(1, 'day');
    }
    
    setDailyForecasts(forecasts);
  }, []);

  // Handle date range selection
  const handleDateRangeChange = useCallback((dates: any) => {
    if (dates && dates[0] && dates[1]) {
      const startDate = dates[0];
      const endDate = dates[1];
      setSelectedDateRange([startDate, endDate]);
      generateDailyForecasts(startDate, endDate);
    } else {
      setSelectedDateRange(null);
      setDailyForecasts([]);
    }
  }, [generateDailyForecasts]);

  // Handle individual daily forecast change
  const handleDailyForecastChange = useCallback((date: string, value: number) => {
    setDailyForecasts(prev => 
      prev.map(item => 
        item.date === date ? { ...item, forecast: value } : item
      )
    );
  }, []);

  // Handle bulk fill
  const handleBulkFill = useCallback(() => {
    if (bulkFillValue === undefined) {
      message.warning('Please enter a bulk fill value');
      return;
    }
    
    setDailyForecasts(prev => 
      prev.map(item => ({ ...item, forecast: bulkFillValue }))
    );
    message.success(`Applied $${bulkFillValue.toLocaleString()} to all ${dailyForecasts.length} days`);
  }, [bulkFillValue, dailyForecasts.length]);

  // Handle clear all
  const handleClearAll = useCallback(() => {
    setDailyForecasts(prev => 
      prev.map(item => ({ ...item, forecast: 0 }))
    );
    message.success('Cleared all daily forecasts');
  }, []);

  // Summary data
  const summaryData = useMemo(() => {
    const totalForecast = dailyForecasts.reduce((sum, item) => sum + item.forecast, 0);
    const daysWithForecast = dailyForecasts.filter(item => item.forecast > 0).length;
    const avgForecast = daysWithForecast > 0 ? totalForecast / daysWithForecast : 0;
    
    return {
      totalDays: dailyForecasts.length,
      daysWithForecast,
      totalForecast,
      avgForecast,
    };
  }, [dailyForecasts]);

  // Handle form submission for mass set
  const handleMassSet = async () => {
    if (!selectedDateRange || dailyForecasts.length === 0) {
      message.warning('Please select a date range first');
      return;
    }

    // Validate that at least one forecast has a value > 0
    const hasValidForecasts = dailyForecasts.some(item => item.forecast > 0);
    if (!hasValidForecasts) {
      message.warning('Please enter at least one forecast value');
      return;
    }

    try {
      // Convert to the format expected by the admin API
      const requestData: RevenueCreateUpdate[] = dailyForecasts
        .filter(item => item.forecast > 0) // Only send non-zero forecasts
        .map(item => ({
          date: item.date,
          type: 'forecast_baseline' as const,
          amount: item.forecast,
        }));

      await massSetMutation.mutateAsync(requestData);
      
      message.success(`Baseline forecasts set for ${requestData.length} days from ${selectedDateRange[0].format('MMM DD')} to ${selectedDateRange[1].format('MMM DD, YYYY')}`);
      
      // Reset form
      setSelectedDateRange(null);
      setDailyForecasts([]);
      setBulkFillValue(undefined);
      
      onSuccess();
    } catch (error) {
      console.error('Error setting baseline forecasts:', error);
    }
  };

  return (
    <Collapse defaultActiveKey={['1']} ghost>
      <Panel 
        header={
          <Space>
            <PlusOutlined />
            <Text strong>Set New Baseline Forecasts</Text>
          </Space>
        } 
        key="1"
      >
        <Card style={{ marginBottom: 16 }}>
          <Form layout="vertical">
            <Row gutter={24}>
              <Col span={12}>
                <Text strong>Step 1: Select Date Range</Text>
                <RangePicker
                  style={{ width: '100%', marginTop: 4 }}
                  value={selectedDateRange}
                  onChange={handleDateRangeChange}
                  placeholder={['Start Date', 'End Date']}
                  format="MMM DD, YYYY"
                />
              </Col>
              
              {dailyForecasts.length > 0 && (
                <Col span={12}>
                  <Text strong>Step 2: Quick Fill Options</Text>
                  <Space style={{ width: '100%', marginTop: 4 }}>
                    <InputNumber
                      placeholder="Amount to fill all"
                      value={bulkFillValue}
                      onChange={(value) => setBulkFillValue(value ?? undefined)}
                      min={0}
                      step={1000}
                      precision={2}
                      style={{ width: 150 }}
                      formatter={currencyFormatterWithSymbol}
                      parser={currencyParser}
                    />
                    <Button
                      icon={<CopyOutlined />}
                      onClick={handleBulkFill}
                      disabled={!bulkFillValue}
                      size="small"
                    >
                      Fill All
                    </Button>
                    <Button
                      icon={<ClearOutlined />}
                      onClick={handleClearAll}
                      size="small"
                    >
                      Clear All
                    </Button>
                  </Space>
                </Col>
              )}
            </Row>

            {dailyForecasts.length > 0 && (
              <>
                <Divider />
                <Text strong>Step 3: Set Daily Forecasts</Text>
                <div style={{ 
                  marginTop: 8, 
                  maxHeight: 300, 
                  overflowY: 'auto', 
                  border: '1px solid #f0f0f0', 
                  borderRadius: 6, 
                  padding: 12,
                  backgroundColor: '#fafafa'
                }}>
                  <Row gutter={[12, 12]}>
                    {dailyForecasts.map((item) => (
                      <DailyForecastInput
                        key={item.date}
                        item={item}
                        onChange={handleDailyForecastChange}
                        formatter={currencyFormatter}
                        parser={currencyParser}
                      />
                    ))}
                  </Row>
                </div>

                {/* Summary */}
                <Row justify="space-between" style={{ marginTop: 12, padding: '8px 12px', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4 }}>
                  <Col>
                    <Text><strong>Total Forecast:</strong> ${summaryData.totalForecast.toLocaleString()}</Text>
                  </Col>
                  <Col>
                    <Text><strong>Days with Forecasts:</strong> {summaryData.daysWithForecast} / {dailyForecasts.length}</Text>
                  </Col>
                </Row>

                <Row justify="end" style={{ marginTop: 16 }}>
                  <Space>
                    <Button onClick={() => {
                      setSelectedDateRange(null);
                      setDailyForecasts([]);
                      setBulkFillValue(undefined);
                    }}>
                      Reset
                    </Button>
                    <Button
                      type="primary"
                      icon={<SaveOutlined />}
                      onClick={handleMassSet}
                      loading={massSetMutation.isPending}
                      disabled={!summaryData.daysWithForecast}
                    >
                      Save Baseline Forecasts
                    </Button>
                  </Space>
                </Row>
              </>
            )}
          </Form>
        </Card>
      </Panel>
    </Collapse>
  );
};

export default BaselineForecastForm;
